import { useState, useEffect } from 'react';
import { message } from 'antd';
import { aiResourceService } from '../../../services/aiResourceService';
import type { AiResource } from '../../../types';
import { prepareResourceForSave } from '../utils';

export const useResource = () => {
  const [resources, setResources] = useState<AiResource[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingResource, setEditingResource] = useState<AiResource | null>(null);
  const [formRef, setFormRef] = useState<any>(null);

  // 加载资源列表
  const loadResources = async () => {
    setLoading(true);
    try {
      const data = await aiResourceService.getAllResources();
      setResources(data);
    } catch (error: any) {
      message.error(error.message || '加载资源列表失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadResources();
  }, []);

  // 打开创建/编辑模态框
  const handleOpenModal = (resource?: AiResource) => {
    setEditingResource(resource || null);
    setModalVisible(true);
  };

  // 关闭模态框
  const handleCloseModal = () => {
    setModalVisible(false);
    setEditingResource(null);
    if (formRef) {
      formRef.resetFields();
    }
  };

  // 保存资源
  const handleSave = async () => {
    if (!formRef) return;
    try {
      const values = await formRef.validateFields();
      // 将 qwenVersion 等表单字段序列化到 config JSON 中
      const saveData = prepareResourceForSave(values, !!editingResource, editingResource);

      if (editingResource) {
        await aiResourceService.updateResource(editingResource.id, saveData);
        message.success('更新成功');
      } else {
        await aiResourceService.createResource(saveData);
        message.success('创建成功');
      }
      handleCloseModal();
      loadResources();
    } catch (error: any) {
      message.error(error.message || '保存失败');
    }
  };

  // 删除资源
  const handleDelete = async (id: string) => {
    try {
      await aiResourceService.deleteResource(id);
      message.success('删除成功');
      loadResources();
    } catch (error: any) {
      message.error(error.message || '删除失败');
    }
  };

  // 测试资源
  const handleTest = async (id: string) => {
    try {
      const result = await aiResourceService.testResource(id);
      if (result.success) {
        message.success(`测试成功：${result.message}`);
        if (result.response) {
          message.info(`响应内容：${result.response}`);
        }
      } else {
        // 显示详细的错误信息
        const errorMsg = result.message || result.error || '测试失败';
        message.error(`测试失败：${errorMsg}`, 5);
        
        // 如果有详细信息，在控制台输出
        if (result.details) {
          console.error('测试失败详情:', result.details);
        }
        if (result.resource) {
          console.error('资源配置:', result.resource);
        }
      }
    } catch (error: any) {
      message.error(error.message || '测试失败', 5);
    }
  };

  return {
    resources,
    loading,
    modalVisible,
    editingResource,
    formRef,
    setFormRef,
    loadResources,
    handleOpenModal,
    handleCloseModal,
    handleSave,
    handleDelete,
    handleTest,
  };
};
